import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Toast from "../../components/Layout/Toast";
import { changePageTitle } from "../preferences/preferencesSlice";
import { fetchEmployees } from "../process/processSlices";
import { Employee } from "../process/types/processTypes";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import {
  createComplaint,
  fetchComplaint,
  selectComplaints,
  updateComplaint,
} from "./complaintSlice";
import {
  ComplainPriority,
  ComplainTypes,
} from "./types/complaintTypes";

export const ComplaintForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id && id !== "new");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const { selectedComplaint, loading, success, error } =
    useAppSelector(selectComplaints);

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );

  const [type, setType] = useState<ComplainTypes>(ComplainTypes.other);
  const [priority, setPriority] = useState<ComplainPriority>(
    ComplainPriority.medium,
  );
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [syncEmployeeStatus, setSyncEmployeeStatus] = useState(false);

  const canSyncDisposition = isPrivilegedTransaction(
    user?.roles ?? [],
    RoleTypes.ChangeEmployeeDisposition,
  );

  useEffect(() => {
    dispatch(changePageTitle(isEdit ? t("editComplaint") : t("addComplaint")));
  }, [dispatch, isEdit, t]);

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchComplaint(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && selectedComplaint) {
      setSelectedEmployee(selectedComplaint.employee ?? null);
      setType(selectedComplaint.type);
      setPriority(selectedComplaint.priority);
      setSubject(selectedComplaint.subject);
      setDescription(selectedComplaint.description);
    }
  }, [isEdit, selectedComplaint]);

  useEffect(() => {
    const term = employeeSearch.trim();
    if (term.length <= 1) {
      setEmployeeOptions([]);
      setEmployeeLoading(false);
      return;
    }

    let active = true;
    setEmployeeLoading(true);
    const timer = setTimeout(async () => {
      const result = await dispatch(
        fetchEmployees({ skip: 0, take: 8, searchText: term }),
      );
      if (!active) return;
      if (fetchEmployees.fulfilled.match(result)) {
        setEmployeeOptions(result.payload?.employees ?? []);
      } else {
        setEmployeeOptions([]);
      }
      setEmployeeLoading(false);
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [dispatch, employeeSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee?._id) return;

    if (isEdit && id) {
      const result = await dispatch(
        updateComplaint({
          _id: id,
          type,
          priority,
          subject,
          description,
        }),
      );
      if (updateComplaint.fulfilled.match(result)) {
        navigate(`/app/complaints/${id}`);
      }
      return;
    }

    const result = await dispatch(
      createComplaint({
        employeeId: selectedEmployee._id,
        type,
        priority,
        subject,
        description,
        syncEmployeeStatus: syncEmployeeStatus || undefined,
      }),
    );
    if (createComplaint.fulfilled.match(result) && result.payload._id) {
      navigate(`/app/complaints/${result.payload._id}`);
    }
  };

  const isSubmitting = loading === "pending";

  return (
    <>
      <Helmet>
        <title>
          {isEdit ? t("editComplaint") : t("addComplaint")} | CvPlus
        </title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isEdit ? t("editComplaint") : t("addComplaint")}
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {!isEdit && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={employeeOptions}
                    filterOptions={(x) => x}
                    loading={employeeLoading}
                    value={selectedEmployee}
                    onChange={(_, value) => setSelectedEmployee(value)}
                    inputValue={employeeSearch}
                    onInputChange={(_, v) => setEmployeeSearch(v)}
                    getOptionLabel={(option) =>
                      `${option.givenName ?? ""} ${option.surName ?? ""}`.trim() ||
                      option.passportNumber ||
                      ""
                    }
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={option.facePhoto ?? ""}
                            sx={{ width: 28, height: 28 }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {`${option.givenName ?? ""} ${option.surName ?? ""}`.trim()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.passportNumber}
                            </Typography>
                          </Box>
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={t("employee")}
                        placeholder={t("searchEmployeeHint")}
                      />
                    )}
                  />
                </Grid>
              )}
              {isEdit && selectedEmployee && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t("employee")}:{" "}
                    {`${selectedEmployee.givenName ?? ""} ${selectedEmployee.surName ?? ""}`.trim()}
                    {selectedEmployee.passportNumber
                      ? ` (${selectedEmployee.passportNumber})`
                      : ""}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>{t("type")}</InputLabel>
                  <Select
                    label={t("type")}
                    value={type}
                    onChange={(e) => setType(e.target.value as ComplainTypes)}
                  >
                    {Object.values(ComplainTypes).map((ty) => (
                      <MenuItem key={ty} value={ty}>
                        {te(ty)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>{t("priority")}</InputLabel>
                  <Select
                    label={t("priority")}
                    value={priority}
                    onChange={(e) =>
                      setPriority(e.target.value as ComplainPriority)
                    }
                  >
                    {Object.values(ComplainPriority).map((p) => (
                      <MenuItem key={p} value={p}>
                        {te(p)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t("subject")}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  label={t("description")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  size="small"
                />
              </Grid>
              {!isEdit && (
                <Grid item xs={12}>
                  <Tooltip
                    title={
                      canSyncDisposition
                        ? t("syncEmployeeDispositionHint")
                        : t("syncEmployeeDispositionDenied")
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={syncEmployeeStatus}
                          onChange={(e) =>
                            setSyncEmployeeStatus(e.target.checked)
                          }
                          disabled={!canSyncDisposition}
                        />
                      }
                      label={t("syncEmployeeDisposition")}
                    />
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={<Save />}
                    disabled={isSubmitting || (!isEdit && !selectedEmployee)}
                  >
                    {t("save")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(isEdit && id ? `/app/complaints/${id}` : "/app/complaints")
                    }
                  >
                    {t("cancel")}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
      {success?.message && (
        <Toast severity="success">{success.message}</Toast>
      )}
      {error?.message && <Toast severity="error">{error.message}</Toast>}
    </>
  );
};

export default ComplaintForm;
